import type { TeamRecord } from '../../shared/types';

export const fmtPoints = (n: number): string =>
  n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtPointsShort = (n: number): string =>
  n.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

export function fmtRecord(r: TeamRecord): string {
  return r.ties > 0 ? `${r.wins}-${r.losses}-${r.ties}` : `${r.wins}-${r.losses}`;
}

export function winPct(r: TeamRecord): number {
  const games = r.wins + r.losses + r.ties;
  return games === 0 ? 0 : (r.wins + r.ties * 0.5) / games;
}

/**
 * Share of available points actually scored -- fpts / ppts, where ppts is the
 * score an optimal starting lineup would have produced. Anything under ~90%
 * means real points died on the bench.
 */
export function efficiency(r: TeamRecord): number | null {
  if (!r.potentialPoints) return null;
  return r.pointsFor / r.potentialPoints;
}

export const fmtPct = (n: number, digits = 1): string => `${(n * 100).toFixed(digits)}%`;

/**
 * A probability, rounded to whole percent without manufacturing certainty.
 *
 * `fmtPct(p, 0)` turns 0.997 into "100%" and 0.003 into "0%", which asserts an
 * impossibility: no fantasy matchup is decided before kickoff, and no playoff
 * race is settled by a simulation merely failing to find the exception in ten
 * thousand tries. The underlying models never return exactly 0 or 1 for a
 * finite input -- only the rounding does, so the rounding is where it has to be
 * caught. A genuinely settled outcome is a separate claim and its callers say
 * so in words ("In", "Out") rather than by printing 100%.
 */
export function fmtOdds(p: number): string {
  if (Number.isNaN(p)) return '—';
  if (p >= 0.995) return '>99%';
  if (p <= 0.005) return '<1%';
  return fmtPct(p, 0);
}

export function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

const DATE = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const DATETIME = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

export const fmtDate = (ms: number): string => DATE.format(new Date(ms));
export const fmtDateTime = (ms: number): string => DATETIME.format(new Date(ms));

export function fmtRelative(ms: number): string {
  const diff = Date.now() - ms;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return fmtDate(ms);
}

/** Sleeper serves manager and league avatars from its own CDN by hash id. */
export function avatarUrl(id: string | null, size: 'thumb' | 'full' = 'thumb'): string | null {
  if (!id) return null;
  return size === 'thumb'
    ? `https://sleepercdn.com/avatars/thumbs/${id}`
    : `https://sleepercdn.com/avatars/${id}`;
}

/** A dynasty pick reads as "2027 1st", not "round 1 of 2027". */
export function pickLabel(season: string, round: number): string {
  return `${season} ${ordinal(round)}`;
}

export const POSITION_ORDER = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'] as const;

export function positionRank(pos: string): number {
  const i = (POSITION_ORDER as readonly string[]).indexOf(pos);
  return i === -1 ? POSITION_ORDER.length : i;
}
